import { Component, OnInit, inject, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AWSLambda } from '../../../AWSLambda.service';
import { SpinnerService } from '../../../components/spinner/spinner.service';
import { FilterField } from '../../../components/generic-list/filter-field.model';
import { BoRolesCheckService } from '../../../components/rolesCheck/bo-roles-check.service';
import { MatSnackBar } from '@angular/material/snack-bar';

// BTB Components
import {
  BtbDataService,
  PartnerInfo,
  TabConfig,
  Invoice,
  InvoiceAction,
  BtbInvoicesListComponent,
  BtbTransactionsListComponent
} from '../../../components/btb';

@Component({
  selector: 'app-partner-detail',
  templateUrl: './partner-detail.component.html',
  styles: [`
    .dashboard-box {
      background: white;
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  `]
})
export class PartnerDetailComponent implements OnInit {
  @ViewChild(BtbInvoicesListComponent) invoicesListComponent!: BtbInvoicesListComponent;
  
  @ViewChild(BtbTransactionsListComponent)
  transactionsListComponent!: BtbTransactionsListComponent;

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private lambda = inject(AWSLambda);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  spinnerService = inject(SpinnerService);
  rolesCheck = inject(BoRolesCheckService);
  private snackBar = inject(MatSnackBar);
  private btbDataService = inject(BtbDataService);


  fechaini: string = '';
  fechafin: string = '';

  partnerId: string = '';
  userId: string = '';
  partnerData: any = null;
  canAccess = false;
  numClients: number = 0;
  allInvoices: any[] = [];
  condicionesComision: string[] = [];
  totalBilling: number = 0;
  pendingTotal: number = 0;
  activeTab: string = 'transactions';

  filteredCounts = {
    facturasLiquidadas: 0,
    facturasPendientes: 0,
    userTransactions: 0
  };

  filterFields: FilterField[] = [];
  filterTransactionsFields: FilterField[] = [];

  userTransactions: any[] = [];

  partnerInfo: PartnerInfo = {
    numClients: 0,
    pendingTotal: 0,
    totalBilling: 0
  };

  tabs: TabConfig[] = [
    {
      id: 'transactions',
      label: 'Transacciones',
      icon: 'account_balance_wallet',
      translationKey: 'BTB.DASHBOARD.PARTNER_DETAIL.TABS.TRANSACTIONS'
    },
    {
      id: 'invoices',
      label: 'Facturas',
      icon: 'receipt',
      translationKey: 'BTB.DASHBOARD.PARTNER_DETAIL.TABS.INVOICES'
    },
    {
      id: 'commission',
      label: 'Comisiones',
      icon: 'percent',
      translationKey: 'BTB.DASHBOARD.PARTNER_DETAIL.TABS.COMMISSION'
    }
  ];

  async ngOnInit() {
    this.canAccess = await this.rolesCheck.checkAccess();

    if (this.canAccess) {
      this.spinnerService.show('partner-detail');

      try {
        this.userId = this.route.snapshot.paramMap.get('userId') || '';

        await this.loadPartnerData();
        await this.loadAllData();
        await this.initializeFilterFields();


        // Las transacciones se cargarán automáticamente desde el componente hijo
        this.userTransactions = [];

        this.updatePartnerInfo();

      } catch (error) {
        console.error('Error initializing partner detail:', error);
      } finally {
        this.spinnerService.hide('partner-detail');
      }
    }
  }

  async loadPartnerData() {
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/get/userDetails', {
        queryStringParameters: {
          sub: this.userId
        }
      });

      this.partnerData = response;
      this.partnerId = response['custom:btbPartnerId'] || '';

    } catch (error) {
      console.error('Error loading partner data:', error);
    }
  }

  private async loadAllData() {
    try {
      const [numClients, allInvoices, conditions, billingTotals] = await Promise.all([
        this.btbDataService.loadClientCount(this.userId),
        this.btbDataService.loadAllInvoices(this.userId),
        this.btbDataService.loadConditions(this.userId),
        this.btbDataService.loadBillingTotals(this.userId)
      ]);

      this.numClients = numClients;
      this.allInvoices = [...allInvoices];
      this.condicionesComision = [...conditions];
      this.totalBilling = billingTotals.totalBilling;
      this.pendingTotal = billingTotals.pendingBilling;

      this.cdr.detectChanges();

    } catch (error) {
      console.error('Error loading data:', error);
    }
  }

  private async initializeFilterFields() {
    try {
      const filters = await this.btbDataService.initializeFilterFields(this.allInvoices, this.userTransactions);

      this.filterFields = filters.filterFields;
      this.filterTransactionsFields = filters.filterTransactionsFields;

      this.cdr.detectChanges();

    } catch (error) {
      console.error('Error initializing filter fields:', error);
    }
  }

  private updatePartnerInfo() {
    this.partnerInfo = {
      name: this.getFullName(),
      phone: this.partnerData?.phone || '',
      email: this.partnerData?.email || '',
      partnerId: this.partnerId,
      numClients: this.numClients,
      pendingTotal: this.pendingTotal,
      totalBilling: this.totalBilling
    };
  }

  getFullName(): string {
    if (!this.partnerData) return '';

    const name = this.partnerData.name || '';
    const middleName = this.partnerData.middle_name || '';

    const nameParts = [name, middleName].filter(part => part && part.trim() !== '');
    return nameParts.length > 0 ? nameParts.join(' ') : '';
  }

  goBack() {
    this.router.navigate(['/backoffice/partners']);
  }

  onTabChange(tabId: string) {
    this.activeTab = tabId;
  }

  onInvoiceAction(action: InvoiceAction) {
    switch (action.action) {
      case 'download':
        this.downloadInvoice(action.invoice);
        break;
      case 'mark_as_pending':
        this.markAsPending(action.invoice);
        break;
      case 'mark_as_settled':
        this.markAsSettled(action.invoice);
        break;
      case 'cancel_invoice':
        this.cancelInvoice(action.invoice);
        break;
      default:
        console.log('Action not implemented:', action.action);
    }
  }

  onFilteredCountChange(data: {type: string, count: number}) {
    setTimeout(() => {
      this.filteredCounts[data.type as keyof typeof this.filteredCounts] = data.count;
      this.cdr.detectChanges();
    }, 0);
  }

  onTransactionsFilteredCountChange(count: number) {
    setTimeout(() => {
      this.filteredCounts.userTransactions = count;
      this.cdr.detectChanges();
    }, 0);
  }

  async copyRegistrationLink() {
    try {
      const registrationLink = `${window.location.origin}/onboarding/register?code=${this.partnerId}`;
      await navigator.clipboard.writeText(registrationLink);

      this.snackBar.open(
        this.translate.instant('BTB.DASHBOARD.REGISTRATION_LINK_COPIED'),
        'Cerrar',
        {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['success-snackbar']
        }
      );
    } catch (error) {
      console.error('Error copying registration link:', error);
      this.snackBar.open(
        this.translate.instant('BTB.DASHBOARD.ERROR_COPYING_LINK'),
        'Cerrar',
        {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar']
        }
      );
    }
  }

  onEditConditions() {
    this.router.navigate(['/backoffice/conditions/partner'], {
      queryParams: {
        code: this.partnerId
      }
    });
  }

  async downloadInvoice(invoice: any) {
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: {
          method: 'downloadInvoice',
          invoiceId: invoice.id
        }
      });

      if (response.downloadUrl) {
        const fetchResponse = await fetch(response.downloadUrl);
        const blob = await fetchResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.fileName || `factura_${invoice.id}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('No se recibió URL de descarga');
      }

    } catch (error) {
      console.error('Error downloading invoice:', error);
      this.snackBar.open('Error al descargar la factura', 'Cerrar', { duration: 3000 });
    } finally {
      // Notificar al componente de listado que termine el procesamiento
      if (this.invoicesListComponent) {
        this.invoicesListComponent.stopProcessing(invoice.id);
      }
    }
  }

  viewInvoice(invoice: any) {
    console.log('View invoice:', invoice);
  }

  editInvoice(invoice: any) {
    console.log('Edit invoice:', invoice);
  }

  deleteInvoice(invoice: any) {
    console.log('Delete invoice:', invoice);
  }

  approveInvoice(invoice: any) {
    console.log('Approve invoice:', invoice);
  }

  rejectInvoice(invoice: any) {
    console.log('Reject invoice:', invoice);
  }

  async markAsPending(invoice: any) {
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: {
          method: 'updateStatusAdmin',
          invoiceId: invoice.id,
          status: 'PENDING'
        }
      });

      if (response.success) {
        this.snackBar.open('Factura marcada como pendiente correctamente', 'Cerrar', { duration: 3000 });
        await this.loadAllData();
      } else {
        throw new Error(response.error || 'Error al marcar como pendiente');
      }
    } catch (error) {
      console.error('Error marking as pending:', error);
      this.snackBar.open('Error al marcar como pendiente', 'Cerrar', { duration: 3000 });
    } finally {
      if (this.invoicesListComponent) {
        this.invoicesListComponent.stopProcessing(invoice.id);
      }
    }
  }

  async markAsSettled(invoice: any) {
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: {
          method: 'updateStatusAdmin',
          invoiceId: invoice.id,
          status: 'SETTLED'
        }
      });

      if (response.success) {
        this.snackBar.open('Factura marcada como liquidada correctamente', 'Cerrar', { duration: 3000 });
        await this.loadAllData();
      } else {
        throw new Error(response.error || 'Error al marcar como liquidada');
      }
    } catch (error) {
      console.error('Error marking as settled:', error);
      this.snackBar.open('Error al marcar como liquidada', 'Cerrar', { duration: 3000 });
    } finally {
      if (this.invoicesListComponent) {
        this.invoicesListComponent.stopProcessing(invoice.id);
      }
    }
  }

  async cancelInvoice(invoice: any) {
    try {
      const response: any = await this.lambda.getLambdaFunction('lambda', '/btb/api', {
        queryStringParameters: {
          method: 'updateStatusAdmin',
          invoiceId: invoice.id,
          status: 'CANCELLED'
        }
      });

      if (response.success) {
        this.snackBar.open('Factura cancelada correctamente', 'Cerrar', { duration: 3000 });
        await this.loadAllData();
      } else {
        throw new Error(response.error || 'Error al cancelar la factura');
      }
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      this.snackBar.open('Error al cancelar la factura', 'Cerrar', { duration: 3000 });
    } finally {
      if (this.invoicesListComponent) {
        this.invoicesListComponent.stopProcessing(invoice.id);
      }
    }
  }


  async onBuscarPorFechas(fechas: {fechaini: string, fechafin: string}) {
    const start = this.formatDateStart(fechas.fechaini);
    const end = this.formatDateEnd(fechas.fechafin);

    // No limpiar las transacciones inmediatamente para mantener el contador
    // Las transacciones se actualizarán cuando lleguen los nuevos datos

    try {
      const transactions = await this.btbDataService.getuserRefTransactions(
        this.userId,
        start,
        end
      );
      // Crear nueva referencia para forzar detección de cambios
      this.userTransactions = [...transactions];
      
      await this.initializeFilterFields();
      
      // Actualizar las fechas de la última búsqueda exitosa
      if (this.transactionsListComponent) {
        this.transactionsListComponent.updateLastSearchDates(fechas.fechaini, fechas.fechafin);
      }
      
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error buscando por fechas', error);
      // Mostrar mensaje de error al usuario
      this.snackBar.open('Error al cargar las transacciones. Por favor, inténtelo de nuevo.', 'Cerrar', {
        duration: 5000,
        panelClass: ['error-snackbar']
      });
      // Asegurar que se limpia en caso de error con nueva referencia
      this.userTransactions = [];
    } finally {
      if (this.transactionsListComponent) {
        this.transactionsListComponent.stopLoading();
      }
    }
  }

  // Funciones auxiliares:
  private formatDateStart(dateStr: string): string {
    // si ya viene con hora, no la toca
    if (dateStr.includes('T')) return dateStr;
    return `${dateStr}T00:00:00Z`;
  }

  private formatDateEnd(dateStr: string): string {
    if (dateStr.includes('T')) return dateStr;
    return `${dateStr}T23:59:59Z`;
  }
}
